import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertCircle,
  CircleSlash,
  ExternalLink,
  FileSearch,
  FolderOpen,
  Loader2,
  X,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { loansService } from '@/services/loans'
import DealFileDialog from '@/components/deal/DealFileDialog'
import { KillDealPanel, type KillDealLoan } from '@/components/deal/KillDealPanel'
import type { LoanFieldLineageField } from '@/pages/pipeline-intelligence/types'
import { FieldStateBadge, type FieldState } from '@/components/fields/fieldState'
import { lineageSummary, stateOfLineage } from '@/components/fields/lineage'
import type { DealRow } from './types'
import { fieldLabel, money, pct, ratio, shortDate } from './format'

function renderValue(field: LoanFieldLineageField): string {
  const value = field.currentValue
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'number') {
    if (field.fieldName === 'loan_amount' || field.fieldName === 'funded_amount') return money(value)
    if (field.fieldName === 'ltv') return pct(value)
    if (field.fieldName === 'dscr') return ratio(value)
    return String(value)
  }
  return String(value)
}

/** A field holds a figure in one of the four agreed value states. */
const PRESENT_STATES: ReadonlySet<FieldState> = new Set(['stated', 'derived', 'suggested', 'confirmed'])
const NO_FIGURE_STATES: ReadonlySet<FieldState> = new Set(['not_applicable', 'in_unread_model', 'conflicted'])

/** The row's kill record in the shape the shared kill form reads. */
function killRecord(row: DealRow): KillDealLoan {
  const dead = row.deal_status === 'dead'
  return {
    id: row.id,
    stage: (row.stage ?? '') as KillDealLoan['stage'],
    // The form treats a deal as killed when this is set.
    killedAt: dead ? (row.dead_at ?? 'date not recorded') : null,
    killedAtStage: row.killed_at_stage ?? null,
    // The server's label for the code, so a retired code (not on the live
    // picklist the form fetches) still reads as words.
    killReasonCode: row.kill_reason_label ?? row.kill_reason_code ?? null,
    killReasonNote: row.kill_reason_note ?? null,
    killedBy: row.killed_by ?? null,
    killBrokerContacted: row.kill_broker_contacted ?? null,
    killContactMethod: row.kill_contact_method ?? null,
    killContactDate: row.kill_contact_date ?? null,
    deadReason: row.dead_reason,
  }
}

export function DealDrawer({
  row,
  onClose,
  onStatusChanged,
}: {
  row: DealRow
  onClose: () => void
  onStatusChanged: () => void
}) {
  const [dealFileOpen, setDealFileOpen] = useState(false)

  useEffect(() => {
    // Selecting a different row must not leave the previous deal's file open.
    setDealFileOpen(false)
  }, [row.id])

  const lineage = useQuery({
    queryKey: ['db-search', 'lineage', row.id],
    queryFn: () => loansService.getFieldLineage(row.id),
    staleTime: 30_000,
  })

  const fields = lineage.data?.fields ?? []
  // The agreed value state decides, never the raw engine word: a machine
  // backfill stored as "approved" is not a person's confirmation, and a value
  // with no source is a Suggestion, not "Recorded".
  const missing = fields.filter((f) => stateOfLineage(f) === 'missing')
  // No figure, for a stated reason: not applicable to this deal, offered by
  // a model not yet read, or two sources in conflict. These were counted as
  // "Present", so one deal read Present (37) beside a record holding 25 (R9).
  const noFigure = fields.filter((f) => NO_FIGURE_STATES.has(stateOfLineage(f)))
  const present = fields.filter((f) => PRESENT_STATES.has(stateOfLineage(f)))
  const dead = row.deal_status === 'dead'

  return (
    <aside
      aria-label={`Deal detail — ${row.property_name ?? row.id}`}
      className="flex w-[360px] flex-shrink-0 flex-col overflow-hidden rounded-lg border border-amber-300/15 bg-[#0a0a0a]"
    >
      <header className="flex items-start justify-between gap-2 border-b border-amber-300/10 px-3 py-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.16em] text-amber-200/70">
            <FileSearch size={11} /> Deal record
          </div>
          <h2 className="mt-0.5 truncate text-[13px] font-semibold text-stone-100">
            {row.property_name ?? row.id}
          </h2>
          <div className="truncate font-mono text-[10px] text-stone-500">
            {row.id} · {row.stage ?? '—'} · {dead ? 'Dead' : 'Live'}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close deal detail"
          className="rounded p-1 text-stone-500 transition hover:text-amber-200"
        >
          <X size={13} />
        </button>
      </header>

      {/* The drawer reports the record; this opens it for editing. Kept out of
          the header so closing the drawer stays the primary action there. */}
      <div className="border-b border-amber-300/[0.07] px-3 py-2">
        <button
          type="button"
          onClick={() => setDealFileOpen(true)}
          className="flex w-full items-center justify-center gap-1.5 rounded border border-amber-400/35 px-3 py-1.5 text-[11px] font-medium text-amber-300 transition-colors hover:bg-amber-400/10"
        >
          <FolderOpen size={12} />
          Open deal file
        </button>
      </div>

      {dealFileOpen && (
        <DealFileDialog
          loanId={row.id}
          fallbackTitle={row.property_name ?? undefined}
          onClose={() => setDealFileOpen(false)}
        />
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 border-b border-amber-300/[0.07] px-3 py-2.5 text-[11px]">
          {[
            ['Loan amount', money(row.loan_amount)],
            ['LTV / DSCR', `${pct(row.ltv)} / ${ratio(row.dscr)}`],
            ['Deal type', row.asset_type ?? '—'],
            ['Location', row.location ?? '—'],
            ['Sponsor', row.sponsor ?? '—'],
            // The assignee is the deal's owner, the catalogue's Responsible
            // Party; the Analyst and Underwriter are the catalogue's own
            // fields. The assignee printed as 'Analyst' (locator-02).
            ['Responsible Party', row.assignee ?? '—'],
            ['Analyst', row.analyst ?? '—'],
            ['Underwriter', row.underwriter ?? '—'],
            ['Originator', row.originator ?? '—'],
            ['Investor', row.investor ?? '—'],
            ['Capital program', row.capital_program ?? '—'],
            // Received: when the package arrived. Entered: when the record was made.
            ['Received', row.originated ?? '—'],
            ['Entered', shortDate(row.created_at)],
            ['Closed', shortDate(row.funded_at)],
            ['Broker', row.broker_name ?? '—'],
          ].map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="text-[9px] uppercase tracking-[0.12em] text-stone-600">{label}</dt>
              <dd className="truncate text-stone-200" title={String(value)}>
                {value}
              </dd>
            </div>
          ))}
        </dl>

        {/* A dead deal's kill record — reason, note, stage it died at, who
            killed it, broker attestation — and the reopen control. */}
        {dead && (
          <div className="border-b border-amber-300/[0.07] px-3 py-2">
            <KillDealPanel key={row.id} loan={killRecord(row)} onChanged={onStatusChanged} />
            <p className="mt-1 text-[9px] text-stone-600">
              Marked {shortDate(row.dead_at)} · excluded from pipeline reporting, kept searchable
              here.
            </p>
          </div>
        )}

        {/* Extracted vs missing — the point of opening a row. */}
        <div className="px-3 py-2.5">
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/80">
              Field provenance
            </span>
            <span className="font-mono text-[10px] text-stone-500">
              {pct(row.completeness, 0)} complete
            </span>
          </div>
          {/* The grid's measure, with its basis: the grid's Field completeness
              and Missing fields columns count exactly this. */}
          {row.completeness_total ? (
            <p
              className="mb-1.5 text-[9px] text-stone-600"
              title="The measure the grid's Field completeness and Missing fields columns report"
            >
              {row.completeness_populated ?? 0} of {row.completeness_total} catalog fields hold a
              value · {row.missing_fields} missing
            </p>
          ) : (
            <div className="mb-1.5" />
          )}

          {lineage.isLoading ? (
            <p className="flex items-center gap-1.5 py-3 text-[11px] text-stone-500">
              <Loader2 size={11} className="animate-spin" /> Loading provenance…
            </p>
          ) : lineage.isError ? (
            <p className="flex items-start gap-1.5 py-2 text-[11px] text-red-300">
              <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
              Field provenance could not be loaded for this deal.
            </p>
          ) : (
            <>
              {lineage.data?.package ? (
                <div className="mb-2 rounded border border-amber-300/[0.09] bg-black/30 px-2 py-1.5">
                  <div className="text-[9px] uppercase tracking-[0.12em] text-stone-600">
                    Source package
                  </div>
                  <div className="truncate text-[10px] text-stone-300">
                    {lineage.data.package.sourceSubject ??
                      lineage.data.package.sourceType ??
                      'Linked ingestion'}
                  </div>
                  {lineage.data.package.sourceEmail && (
                    <div className="truncate text-[9px] text-stone-600">
                      {lineage.data.package.sourceEmail}
                    </div>
                  )}
                </div>
              ) : (
                <p className="mb-2 flex items-center gap-1.5 text-[10px] text-stone-600">
                  <CircleSlash size={10} /> No source package linked — values were entered directly.
                </p>
              )}

              {/* The lists below cover every field the lineage tracks — the
                  catalog plus desk-defined fields — and count a suggestion
                  as a figure, so their totals are on their own stated basis. */}
              <div className="mb-1 text-[9px] text-stone-600">
                Provenance of {fields.length} tracked fields
              </div>

              {missing.length > 0 && (
                <>
                  <div className="mb-1 text-[9px] uppercase tracking-[0.14em] text-red-300/70">
                    Missing ({missing.length})
                  </div>
                  <ul className="mb-2 space-y-0.5">
                    {missing.map((field) => (
                      <li
                        key={field.fieldName}
                        className="flex items-center justify-between gap-2 rounded bg-red-500/[0.05] px-1.5 py-1"
                      >
                        <span className="truncate text-[11px] text-stone-300">
                          {fieldLabel(field.fieldName)}
                        </span>
                        <FieldStateBadge state="missing" />
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {noFigure.length > 0 && (
                <>
                  <div className="mb-1 text-[9px] uppercase tracking-[0.14em] text-stone-500">
                    No figure ({noFigure.length})
                  </div>
                  <ul className="mb-2 space-y-0.5">
                    {noFigure.map((field) => (
                      <li
                        key={field.fieldName}
                        className="flex items-center justify-between gap-2 rounded px-1.5 py-1"
                      >
                        <span className="truncate text-[11px] text-stone-400">
                          {fieldLabel(field.fieldName)}
                        </span>
                        <FieldStateBadge
                          state={stateOfLineage(field)}
                          title={lineageSummary(field) || null}
                        />
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <div className="mb-1 text-[9px] uppercase tracking-[0.14em] text-stone-600">
                Present ({present.length})
              </div>
              <ul className="space-y-0.5">
                {present.map((field) => (
                  <li key={field.fieldName} className="rounded px-1.5 py-1 hover:bg-white/[0.02]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-[11px] text-stone-400">
                        {fieldLabel(field.fieldName)}
                      </span>
                      <FieldStateBadge state={stateOfLineage(field)} title={lineageSummary(field) || null} />
                    </div>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-[11px] text-stone-100" title={renderValue(field)}>
                        {renderValue(field)}
                      </span>
                      {field.confidence !== null && field.confidence !== undefined && (
                        <span className="flex-shrink-0 font-mono text-[9px] text-stone-600">
                          {(field.confidence * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                    {lineageSummary(field) && (
                      <div className="truncate text-[9px] text-stone-600" title={lineageSummary(field)}>
                        {lineageSummary(field)}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* A dead deal's kill record and reopen control sit in the body above,
          and the Pipeline lists live deals only, so a dead deal has no footer:
          it is opened with "Open deal file". */}
      {!dead && (
        <footer className="space-y-1.5 border-t border-amber-300/10 px-3 py-2.5">
          {/* The same kill form as the Pipeline Inspector: reason picklist,
              note and broker attestation. Free-text "Mark dead" stored none of
              that and left the reason unsearchable (randy-2.7). */}
          <KillDealPanel key={row.id} loan={killRecord(row)} onChanged={onStatusChanged} />
          {/* The Pipeline selects and inspects the deal named in
              `focusLoanId` (usePipelineFocus). */}
          <Link
            to="/pipeline"
            state={{ focusLoanId: row.id }}
            className="flex w-full items-center justify-center gap-1.5 rounded border border-amber-300/15 px-2 py-1.5 text-[11px] text-stone-300 transition hover:border-amber-300/30 hover:text-amber-100"
          >
            <ExternalLink size={11} /> Open in Pipeline
          </Link>
        </footer>
      )}
    </aside>
  )
}
